import { useEffect, useState } from "react";
import { Loader2, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { PAGE_SEO_DEFAULTS } from "@/lib/seo";
import { CollectionTab } from "./CollectionTab";

const PAGES = [
  { key: "home", label: "Home", path: "/" },
  { key: "treatments", label: "Treatments", path: "/treatments" },
  { key: "doctors", label: "Doctors", path: "/doctors" },
  { key: "about", label: "About", path: "/about" },
  { key: "gallery", label: "Results Gallery", path: "/gallery" },
  { key: "blog", label: "Skin Journal", path: "/blog" },
  { key: "contact", label: "Contact & Booking", path: "/contact" },
];

const titleTone = (n) => (n === 0 ? "text-[#91837C]" : n > 60 ? "text-red-500" : n >= 50 ? "text-emerald-600" : "text-amber-600");
const descTone = (n) => (n === 0 ? "bg-[#E8E2D9]" : n > 160 ? "bg-red-500" : n >= 145 ? "bg-emerald-500" : "bg-amber-500");

// SEO command centre: per-page metadata with live counters + SERP previews,
// the global keyword dictionary, and tracking/verification fields.
export const SeoTab = () => {
  const { content, refresh } = useContent();
  const s = content?.settings || {};
  const [pages, setPages] = useState({});
  const [tracking, setTracking] = useState({ ga4_id: "", gtm_id: "", google_ads_id: "", google_ads_label: "", meta_pixel_id: "", gsc_verification: "", gsc_dns_verified: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPages(s.seo_pages || {});
    setTracking({
      ga4_id: s.ga4_id || "",
      gtm_id: s.gtm_id || "",
      google_ads_id: s.google_ads_id || "",
      google_ads_label: s.google_ads_label || "",
      meta_pixel_id: s.meta_pixel_id || "",
      gsc_verification: s.gsc_verification || "",
      gsc_dns_verified: !!s.gsc_dns_verified,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const setField = (key, field, value) => setPages((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        seo_pages: pages,
        ga4_id: tracking.ga4_id.trim(),
        gtm_id: tracking.gtm_id.trim(),
        google_ads_id: tracking.google_ads_id.trim(),
        google_ads_label: tracking.google_ads_label.trim(),
        meta_pixel_id: tracking.meta_pixel_id.trim(),
        gsc_verification: tracking.gsc_verification.trim(),
        gsc_dns_verified: tracking.gsc_dns_verified,
      });
      await refresh();
      toast.success("SEO settings saved");
    } catch {
      toast.error("Could not save SEO settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="seo-tab">
      {/* Page metadata */}
      <div className="glass rounded-2xl p-6">
        <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Page Metadata</h2>
        <p className="mt-1 text-xs font-light text-[#91837C]">
          Titles and descriptions shown in Google results. Aim for 50–60 character titles and 145–160 character descriptions.
        </p>
        <div className="mt-6 space-y-7">
          {PAGES.map((p) => {
            const v = pages[p.key] || {};
            const title = v.title ?? PAGE_SEO_DEFAULTS[p.key].title;
            const desc = v.description ?? PAGE_SEO_DEFAULTS[p.key].description;
            return (
              <div key={p.key} className="rounded-xl border border-[#EBE3DA] bg-white/60 p-5" data-testid={`seo-page-${p.key}`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#B38B2D]">{p.label} <span className="text-[#91837C]">· {p.path}</span></p>
                <div className="mt-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-[#5A504B]" htmlFor={`seo-title-${p.key}`}>SEO Title</label>
                    <span className={`text-[11px] font-semibold tabular-nums ${titleTone(title.length)}`} data-testid={`seo-title-count-${p.key}`}>
                      {title.length}/60
                    </span>
                  </div>
                  <input
                    id={`seo-title-${p.key}`}
                    data-testid={`seo-title-input-${p.key}`}
                    className="input-lux mt-1.5 text-sm"
                    value={title}
                    onChange={(e) => setField(p.key, "title", e.target.value)}
                  />
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-[#5A504B]" htmlFor={`seo-desc-${p.key}`}>Meta Description</label>
                    <span className="text-[11px] font-semibold tabular-nums text-[#91837C]" data-testid={`seo-desc-count-${p.key}`}>{desc.length}/160</span>
                  </div>
                  <textarea
                    id={`seo-desc-${p.key}`}
                    data-testid={`seo-desc-input-${p.key}`}
                    className="input-lux mt-1.5 min-h-[64px] text-sm"
                    value={desc}
                    onChange={(e) => setField(p.key, "description", e.target.value)}
                  />
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#F0EAE2]">
                    <div className={`h-full rounded-full transition-all duration-300 ${descTone(desc.length)}`} style={{ width: `${Math.min(100, (desc.length / 160) * 100)}%` }} />
                  </div>
                </div>
                {/* SERP preview */}
                <div className="mt-4 rounded-lg border border-[#EBE3DA] bg-white p-4">
                  <p className="mb-1.5 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#91837C]">
                    <Search className="h-3 w-3" aria-hidden="true" /> Google preview
                  </p>
                  <p className="truncate text-[15px] leading-snug text-[#1a0dab]">{title || "Page title"}</p>
                  <p className="mt-0.5 text-xs text-[#006621]">royalskincosmeticclinic.com{p.path === "/" ? "" : p.path}</p>
                  <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-[#545454]">{desc || "Meta description"}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tracking & verification */}
      <div className="glass rounded-2xl p-6">
        <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Tracking & Verification</h2>
        <p className="mt-1 text-xs font-light text-[#91837C]">
          Analytics and search-engine verification — pasted here, injected everywhere, never slowing the site down.
        </p>
        <div className="mt-5 grid gap-5 sm:grid-cols-3">
          <div>
            <label className="text-xs font-medium text-[#5A504B]" htmlFor="seo-ga4">Google Analytics 4 ID</label>
            <input
              id="seo-ga4"
              data-testid="seo-ga4-input"
              className="input-lux mt-1.5 text-sm"
              placeholder="G-XXXXXXXXXX"
              value={tracking.ga4_id}
              onChange={(e) => setTracking((t) => ({ ...t, ga4_id: e.target.value }))}
            />
            <p className="mt-1.5 text-[11px] font-light text-[#91837C]">The standalone Google tag (gtag.js).</p>
          </div>
          <div>
            <label className="text-xs font-medium text-[#5A504B]" htmlFor="seo-gtm">Google Tag Manager ID</label>
            <input
              id="seo-gtm"
              data-testid="seo-gtm-input"
              className="input-lux mt-1.5 text-sm"
              placeholder="GTM-XXXXXXX"
              value={tracking.gtm_id}
              onChange={(e) => setTracking((t) => ({ ...t, gtm_id: e.target.value }))}
            />
            <p className="mt-1.5 text-[11px] font-light text-[#91837C]">Container loader — fires whatever tags you manage inside GTM.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-[#5A504B]" htmlFor="seo-ads">Google Ads Tag ID</label>
            <input
              id="seo-ads"
              data-testid="seo-ads-input"
              className="input-lux mt-1.5 text-sm"
              placeholder="AW-XXXXXXXXX"
              value={tracking.google_ads_id}
              onChange={(e) => setTracking((t) => ({ ...t, google_ads_id: e.target.value }))}
            />
            <p className="mt-1.5 text-[11px] font-light text-[#91837C]">For Google Ads campaigns — rides the same Google tag.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-[#5A504B]" htmlFor="seo-ads-label">Ads Conversion Label (optional)</label>
            <input
              id="seo-ads-label"
              data-testid="seo-ads-label-input"
              className="input-lux mt-1.5 text-sm"
              placeholder="e.g. abcDEF123 — from the conversion action"
              value={tracking.google_ads_label}
              onChange={(e) => setTracking((t) => ({ ...t, google_ads_label: e.target.value }))}
            />
            <p className="mt-1.5 text-[11px] font-light text-[#91837C]">When set, every booking fires a Google Ads conversion.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-[#5A504B]" htmlFor="seo-pixel">Meta (Facebook) Pixel ID</label>
            <input
              id="seo-pixel"
              data-testid="seo-pixel-input"
              className="input-lux mt-1.5 text-sm"
              placeholder="16-digit pixel ID"
              value={tracking.meta_pixel_id}
              onChange={(e) => setTracking((t) => ({ ...t, meta_pixel_id: e.target.value }))}
            />
            <p className="mt-1.5 text-[11px] font-light text-[#91837C]">PageViews tracked sitewide; bookings fire a Lead event.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-[#5A504B]" htmlFor="seo-gsc">Search Console Verification Token</label>
            <input
              id="seo-gsc"
              data-testid="seo-gsc-input"
              className="input-lux mt-1.5 text-sm"
              placeholder="From Search Console → Ownership verification → HTML tag"
              value={tracking.gsc_verification}
              onChange={(e) => setTracking((t) => ({ ...t, gsc_verification: e.target.value }))}
            />
            <p className="mt-1.5 text-[11px] font-light leading-relaxed text-[#91837C]">
              Search Console → add property royalskincosmeticclinic.com → "HTML tag" → paste only the content value here, save, then Verify.
            </p>
          </div>
        </div>
      </div>

      {/* Google Search Console — verification path + upcoming dashboard */}
      <div className="glass rounded-2xl p-6" data-testid="gsc-card">
        <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Google Search Console</h2>
        <p className="mt-1 text-xs font-light leading-relaxed text-[#91837C]">
          Your ranking command centre — queries, impressions and positions will appear here once Google is connected.
        </p>
        <label className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm text-[#2D6A4F]" data-testid="gsc-dns-option">
          <input
            type="checkbox"
            checked={tracking.gsc_dns_verified}
            onChange={(e) => setTracking((t) => ({ ...t, gsc_dns_verified: e.target.checked }))}
            data-testid="gsc-dns-checkbox"
            className="mt-0.5 accent-[#2D6A4F]"
          />
          <span>
            <span className="font-semibold">I already verified via my domain provider (DNS record)</span>
            <span className="mt-0.5 block text-xs font-light leading-relaxed">
              Tick this if Search Console shows "You are a verified owner" through the Domain name provider method — your dashboard marks Search Console as connected and you can skip the token steps below.
            </span>
          </span>
        </label>
        <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#91837C]">Or verify with the HTML tag method:</p>
        <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm font-light leading-relaxed text-[#5A504B]">
          <li>Open <span className="font-medium text-[#1A1615]">search.google.com/search-console</span> and add the property <span className="font-medium text-[#1A1615]">royalskincosmeticclinic.com</span></li>
          <li>Choose the <span className="font-medium text-[#1A1615]">HTML tag</span> verification method and copy the long content value</li>
          <li>Paste it in the <span className="font-medium text-[#1A1615]">Verification Token</span> field above and Save</li>
          <li>Back in Search Console, click <span className="font-medium text-[#1A1615]">Verify</span> — then submit the sitemap: <span className="font-medium text-[#1A1615]">https://royalskincosmeticclinic.com/api/sitemap.xml</span></li>
          <li>Tell us when it's verified — we'll connect your Google account and light up the ranking dashboard right here</li>
        </ol>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="seo-save-button" className="btn-gold inline-flex items-center gap-2 px-8 py-3 text-xs disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save SEO Settings
        </button>
      </div>

      {/* Keyword dictionary — contextual auto-linking */}
      <div className="glass rounded-2xl p-6">
        <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Keyword Dictionary — Auto-Linking</h2>
        <p className="mt-1 text-xs font-light leading-relaxed text-[#91837C]">
          When a Journal essay mentions one of these phrases, the first mention becomes a link to the target page
          automatically (max 5 links per essay, headings untouched). Point phrases at your money pages like /treatments or a pillar guide.
        </p>
        <div className="mt-4">
          <CollectionTab collection="seo_keywords" />
        </div>
      </div>

      {/* Custom tags & pixels */}
      <div className="glass rounded-2xl p-6">
        <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Custom Tags & Pixels</h2>
        <p className="mt-1 text-xs font-light leading-relaxed text-[#91837C]">
          Add any third-party tag — Meta Pixel, TikTok, Hotjar, LinkedIn Insight — by pasting its full snippet. Tags inject
          on every page of the site and can be paused anytime without deleting them.
        </p>
        <div className="mt-4">
          <CollectionTab collection="custom_tags" />
        </div>
      </div>
    </div>
  );
};
